import { ZoomIn, ZoomOut } from '@mui/icons-material';
import { Box, IconButton, Stack, Switch, Typography } from '@mui/material';
import { useLayoutEffect, useRef, useState } from 'react';

import {
    buildDocumentContentCss,
    getDocumentCanvasStyle,
    normalizeFontFamilyStyles,
} from './documentContentStyles';
import { PAGE_SIZES } from 'config/defaultBranding';
import {
    COMPANY_NAME_SAMPLE,
    getPlaceholdersForType,
    renderTypedCompanySignature,
} from 'config/placeholders';
import type { Agent, BrandingSettings, PricingConfiguration, TemplateType } from 'types';
import type { AgentSignature } from 'types/signature';
import {
    calculateTotalFee,
    formatCurrency,
    generateMilestoneTableHTML,
} from 'views/retainerAgreement/pricingUtils';

const CSS_PX_PER_INCH = 96;
const PREVIEW_PADDING_PX = 16;
const MIN_ZOOM_PERCENT = 25;
const MAX_ZOOM_PERCENT = 200;

const cssLengthToPx = (value: string): number => {
    if (value.endsWith('in')) {
        return parseFloat(value) * CSS_PX_PER_INCH;
    }
    if (value.endsWith('px')) {
        return parseFloat(value);
    }
    return parseFloat(value);
};

interface LivePreviewPanelProps {
    htmlContent: string;
    branding: BrandingSettings;
    pricingConfig?: PricingConfiguration;
    country?: string;
    program?: string;
    agentData?: Agent;
    agentSignature?: AgentSignature | null;
    templateType?: TemplateType;
}

export function LivePreviewPanel({
    htmlContent,
    branding,
    pricingConfig,
    country,
    program,
    agentData,
    agentSignature,
    templateType = 'visa-application',
}: LivePreviewPanelProps) {
    const [useSampleData, setUseSampleData] = useState(true);
    const [manualZoom, setManualZoom] = useState<number | null>(null);
    const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
    const viewportRef = useRef<HTMLDivElement>(null);
    const pageBoxRef = useRef<HTMLDivElement>(null);
    const [pageBoxSize, setPageBoxSize] = useState({ width: 0, height: 0 });

    const pageSize = PAGE_SIZES[branding.pageSize || 'letter'];
    const pageWidthPx = cssLengthToPx(pageSize.width);
    const pageHeightPx = cssLengthToPx(pageSize.height);

    useLayoutEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport) {
            return undefined;
        }

        const updateSize = () => {
            setViewportSize({ width: viewport.clientWidth, height: viewport.clientHeight });
        };

        updateSize();
        const observer = new ResizeObserver(updateSize);
        observer.observe(viewport);
        return () => observer.disconnect();
    }, []);

    useLayoutEffect(() => {
        setManualZoom(null);
    }, [branding.pageSize]);

    useLayoutEffect(() => {
        const pageBox = pageBoxRef.current;
        if (!pageBox) {
            return undefined;
        }

        const updateSize = () => {
            setPageBoxSize({
                width: pageBox.scrollWidth,
                height: pageBox.scrollHeight,
            });
        };

        updateSize();
        const observer = new ResizeObserver(updateSize);
        observer.observe(pageBox);
        return () => observer.disconnect();
    }, [pageWidthPx, pageHeightPx, htmlContent, branding, useSampleData]);

    const fitZoomPercent =
        viewportSize.width > 0 && viewportSize.height > 0
            ? Math.max(
                  MIN_ZOOM_PERCENT,
                  Math.min(
                      100,
                      Math.floor(
                          Math.min(
                              (viewportSize.width - PREVIEW_PADDING_PX * 2) / pageWidthPx,
                              (viewportSize.height - PREVIEW_PADDING_PX * 2) / pageHeightPx
                          ) * 100
                      )
                  )
              )
            : 50;

    const zoom = manualZoom ?? fitZoomPercent;
    const scale = zoom / 100;
    const scaledPageWidth = Math.max(pageWidthPx, pageBoxSize.width || pageWidthPx) * scale;
    const scaledPageHeight = Math.max(pageHeightPx, pageBoxSize.height || pageHeightPx) * scale;

    const renderSignature = (signature: AgentSignature): string => {
        const sizeMap = {
            small: '24px',
            medium: '32px',
            large: '40px',
        };

        const fontSize = sizeMap[signature.size] || sizeMap.medium;

        switch (signature.creation_method) {
            case 'typed':
                if (signature.typed_text && signature.font_family) {
                    return `<span class="placeholder-value" style="font-family: ${signature.font_family}, cursive; font-size: ${fontSize}; color: ${signature.color};">${signature.typed_text}</span>`;
                }
                break;
            case 'drawn':
                if (signature.drawn_svg_data) {
                    return `<div class="placeholder-value" style="display: inline-block; height: ${fontSize};">${signature.drawn_svg_data}</div>`;
                }
                break;
            case 'uploaded':
                if (signature.uploaded_image_base64) {
                    const mimeType = signature.uploaded_image_mime_type || 'image/png';
                    return `<img src="data:${mimeType};base64,${signature.uploaded_image_base64}" alt="Signature" style="height: ${fontSize}; display: inline-block;" class="placeholder-value" />`;
                }
                break;
        }
        return '<span class="placeholder-value" style="font-style: italic; color: #9ca3af;">[No signature]</span>';
    };

    const replacePlaceholders = (html: string): string => {
        if (!useSampleData) return html;

        let result = html;

        // Replace program placeholders with actual selected values
        if (country) {
            result = result.replace(
                /\{\{program\.country\}\}/g,
                `<span class="placeholder-value">${country}</span>`
            );
        }
        if (program) {
            result = result.replace(
                /\{\{program\.name\}\}/g,
                `<span class="placeholder-value">${program}</span>`
            );
        }

        // Replace agent placeholders with actual selected agent data
        if (agentData) {
            if (agentData.firstname && agentData.lastname) {
                const nameParts = [
                    agentData.firstname,
                    agentData.middlename,
                    agentData.lastname,
                ].filter(Boolean);
                const fullName = nameParts.join(' ');
                result = result.replace(
                    /\{\{agent\.name\}\}/g,
                    `<span class="placeholder-value">${fullName}</span>`
                );

                // Replace signature with actual signature if available
                if (agentSignature) {
                    result = result.replace(
                        /\{\{agent\.signature\}\}/g,
                        renderSignature(agentSignature)
                    );
                } else {
                    // Fallback to name in cursive
                    result = result.replace(
                        /\{\{agent\.signature\}\}/g,
                        `<span class="placeholder-value" style="font-family: Allura, cursive; font-size: 32px;">${fullName}</span>`
                    );
                }
            }
            if (agentData.email) {
                result = result.replace(
                    /\{\{agent\.email\}\}/g,
                    `<span class="placeholder-value">${agentData.email}</span>`
                );
            }
            if (agentData.phone_number) {
                result = result.replace(
                    /\{\{agent\.phone\}\}/g,
                    `<span class="placeholder-value">${agentData.phone_number}</span>`
                );
            }
            // Agent title - can be constructed from available data if needed
            if (agentData.firstname) {
                result = result.replace(
                    /\{\{agent\.title\}\}/g,
                    `<span class="placeholder-value">Immigration Consultant</span>`
                );
            }
        }

        result = result.replace(
            /\{\{company\.name\}\}/g,
            `<span class="placeholder-value">${COMPANY_NAME_SAMPLE}</span>`
        );
        result = result.replace(
            /\{\{company\.signature\}\}/g,
            `<span class="placeholder-value">${renderTypedCompanySignature(COMPANY_NAME_SAMPLE)}</span>`
        );

        if (pricingConfig) {
            const totalFee = calculateTotalFee(pricingConfig);

            result = result.replace(
                /\{\{payment\.total_fee\}\}/g,
                formatCurrency(totalFee, pricingConfig.currency)
            );
            result = result.replace(/\{\{payment\.currency\}\}/g, pricingConfig.currency);

            if (pricingConfig.model === 'hourly' && pricingConfig.hourly) {
                result = result.replace(
                    /\{\{payment\.hourly_rate\}\}/g,
                    formatCurrency(pricingConfig.hourly.hourlyRate, pricingConfig.currency)
                );
                result = result.replace(
                    /\{\{payment\.estimated_hours\}\}/g,
                    pricingConfig.hourly.estimatedHours.toString()
                );
                result = result.replace(
                    /\{\{payment\.estimated_total\}\}/g,
                    formatCurrency(totalFee, pricingConfig.currency)
                );
            }

            if (pricingConfig.model === 'hybrid' && pricingConfig.hybrid) {
                result = result.replace(
                    /\{\{payment\.upfront_fee\}\}/g,
                    formatCurrency(pricingConfig.hybrid.upfrontFee, pricingConfig.currency)
                );
                const milestoneTotal = pricingConfig.hybrid.milestones.reduce(
                    (sum, m) => sum + (m.amount || 0),
                    0
                );
                result = result.replace(
                    /\{\{payment\.milestone_total\}\}/g,
                    formatCurrency(milestoneTotal, pricingConfig.currency)
                );
            }

            if (pricingConfig.model === 'milestone' && pricingConfig.milestone) {
                const milestoneTable = generateMilestoneTableHTML(
                    pricingConfig.milestone.milestones,
                    pricingConfig.currency,
                    pricingConfig.milestone.displayMode,
                    pricingConfig.milestone.totalFee
                );
                result = result.replace(/\{\{retainer\.milestones_table\}\}/g, milestoneTable);
            }

            if (pricingConfig.model === 'hybrid' && pricingConfig.hybrid) {
                const milestoneTable = generateMilestoneTableHTML(
                    pricingConfig.hybrid.milestones,
                    pricingConfig.currency,
                    pricingConfig.hybrid.displayMode,
                    pricingConfig.hybrid.upfrontFee
                );
                result = result.replace(/\{\{retainer\.milestones_table\}\}/g, milestoneTable);
            }
        }

        getPlaceholdersForType(templateType).forEach((placeholder) => {
            const regex = new RegExp(placeholder.token.replace(/[{}]/g, '\\$&'), 'g');
            result = result.replace(
                regex,
                `<span class="placeholder-value">${placeholder.sampleValue}</span>`
            );
        });

        return result;
    };

    const canvasStyle = getDocumentCanvasStyle(branding);

    const getHeaderLogoStyle = (): React.CSSProperties => {
        if (!branding.headerLogo) return {};

        // Size mapping for potential future use
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const sizeMap = {
            small: '60px',
            medium: '100px',
            large: '140px',
        };

        return {
            textAlign: branding.headerLogo.position,
            marginBottom: '20px',
        };
    };

    const getFooterLogoStyle = (): React.CSSProperties => {
        if (!branding.footerLogo) return {};

        // Size mapping for potential future use
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const sizeMap = {
            small: '40px',
            medium: '60px',
            large: '80px',
        };

        return {
            textAlign: branding.footerLogo.position,
            marginTop: '20px',
        };
    };

    const processedContent = normalizeFontFamilyStyles(replacePlaceholders(htmlContent));

    return (
        <Box
            sx={{
                height: '100%',
                minHeight: 0,
                minWidth: 0,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Stack
                direction="row"
                spacing={1}
                sx={{
                    alignItems: 'center',
                    px: 1.5,
                    py: 0.75,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    flexShrink: 0,
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    Live Preview
                </Typography>
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'center', ml: 'auto', minWidth: 0 }}
                >
                    <Stack direction="row" sx={{ alignItems: 'center' }}>
                        <Switch
                            id="sample-data"
                            size="small"
                            checked={useSampleData}
                            onChange={() => setUseSampleData(!useSampleData)}
                            sx={{ ml: -0.5 }}
                        />
                        <Typography component="span" sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                            Show Sample Data
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                        <IconButton
                            size="small"
                            aria-label="Zoom out"
                            onClick={() => setManualZoom(Math.max(MIN_ZOOM_PERCENT, zoom - 10))}
                            disabled={zoom <= MIN_ZOOM_PERCENT}
                            sx={{ color: '#3c4043' }}
                        >
                            <ZoomOut fontSize="small" />
                        </IconButton>
                        <Typography variant="caption" sx={{ width: 40, textAlign: 'center' }}>
                            {zoom}%
                        </Typography>
                        <IconButton
                            size="small"
                            aria-label="Zoom in"
                            onClick={() => setManualZoom(Math.min(MAX_ZOOM_PERCENT, zoom + 10))}
                            disabled={zoom >= MAX_ZOOM_PERCENT}
                            sx={{ color: '#3c4043' }}
                        >
                            <ZoomIn fontSize="small" />
                        </IconButton>
                    </Stack>
                </Stack>
            </Stack>

            <Box
                ref={viewportRef}
                sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: 'auto', bgcolor: 'grey.100' }}
            >
                <div
                    style={{
                        boxSizing: 'border-box',
                        display: 'flex',
                        width: 'max-content',
                        minWidth: '100%',
                        minHeight: '100%',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: PREVIEW_PADDING_PX,
                    }}
                >
                    <div
                        style={{
                            width: scaledPageWidth,
                            height: scaledPageHeight,
                            flexShrink: 0,
                            position: 'relative',
                        }}
                    >
                        <div
                            ref={pageBoxRef}
                            className="bg-white"
                            style={{
                                width: pageSize.width,
                                boxShadow:
                                    '0 1px 3px rgba(60, 64, 67, 0.3), 0 4px 8px 3px rgba(60, 64, 67, 0.15)',
                                minHeight: pageSize.height,
                                backgroundColor: canvasStyle.backgroundColor,
                                transform: `scale(${scale})`,
                                transformOrigin: 'top left',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                            }}
                        >
                            <div style={canvasStyle}>
                                {branding.headerLogo && (
                                    <div style={getHeaderLogoStyle()}>
                                        <img
                                            src={branding.headerLogo.url}
                                            alt="Header logo"
                                            style={{
                                                maxHeight:
                                                    branding.headerLogo.size === 'small'
                                                        ? '60px'
                                                        : branding.headerLogo.size === 'medium'
                                                          ? '100px'
                                                          : '140px',
                                                display:
                                                    branding.headerLogo.position === 'center'
                                                        ? 'inline-block'
                                                        : 'block',
                                            }}
                                        />
                                    </div>
                                )}

                                {branding.header?.enabled && (
                                    <div
                                        style={{
                                            borderBottom: '2px dashed #9ca3af',
                                            marginBottom: '20px',
                                            padding: `${branding.header.paddingTop || 12}pt 0 ${branding.header.paddingBottom || 12}pt`,
                                            fontSize: `${branding.header.fontSize || 10}pt`,
                                            color: branding.header.textColor || '#666666',
                                            textAlign: branding.header.textAlign || 'center',
                                            backgroundColor: branding.header.backgroundColor,
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontSize: '9px',
                                                color: '#9ca3af',
                                                marginBottom: '8px',
                                            }}
                                        >
                                            [Repeating Header - appears on all pages]
                                        </div>
                                        {branding.header.image && (
                                            <div
                                                style={{
                                                    textAlign: branding.header.image.position,
                                                }}
                                            >
                                                <img
                                                    src={branding.header.image.url}
                                                    alt="Header"
                                                    style={{
                                                        maxHeight: branding.header.image.maxHeight,
                                                        display: 'inline-block',
                                                    }}
                                                />
                                            </div>
                                        )}
                                        {branding.header.content && (
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: normalizeFontFamilyStyles(
                                                        replacePlaceholders(branding.header.content)
                                                    ),
                                                }}
                                            />
                                        )}
                                    </div>
                                )}

                                <div
                                    dangerouslySetInnerHTML={{ __html: processedContent }}
                                    className="preview-content"
                                />

                                {branding.footerLogo && (
                                    <div style={getFooterLogoStyle()}>
                                        <img
                                            src={branding.footerLogo.url}
                                            alt="Footer logo"
                                            style={{
                                                maxHeight:
                                                    branding.footerLogo.size === 'small'
                                                        ? '40px'
                                                        : branding.footerLogo.size === 'medium'
                                                          ? '60px'
                                                          : '80px',
                                                display:
                                                    branding.footerLogo.position === 'center'
                                                        ? 'inline-block'
                                                        : 'block',
                                            }}
                                        />
                                    </div>
                                )}

                                {branding.footer?.enabled && (
                                    <div
                                        style={{
                                            borderTop: '2px dashed #9ca3af',
                                            marginTop: '20px',
                                            padding: `${branding.footer.paddingTop || 12}pt 0 ${branding.footer.paddingBottom || 12}pt`,
                                            fontSize: `${branding.footer.fontSize || 9}pt`,
                                            color: branding.footer.textColor || '#666666',
                                            textAlign: branding.footer.textAlign || 'center',
                                            backgroundColor: branding.footer.backgroundColor,
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontSize: '9px',
                                                color: '#9ca3af',
                                                marginBottom: '8px',
                                            }}
                                        >
                                            [Repeating Footer - appears on all pages]
                                        </div>
                                        {branding.footer.content && (
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: normalizeFontFamilyStyles(
                                                        replacePlaceholders(branding.footer.content)
                                                    ),
                                                }}
                                            />
                                        )}
                                        {branding.footer.image && (
                                            <div
                                                style={{
                                                    textAlign: branding.footer.image.position,
                                                }}
                                            >
                                                <img
                                                    src={branding.footer.image.url}
                                                    alt="Footer"
                                                    style={{
                                                        maxHeight: branding.footer.image.maxHeight,
                                                        display: 'inline-block',
                                                    }}
                                                />
                                            </div>
                                        )}
                                        {branding.footer.pageNumbers?.enabled && (
                                            <div
                                                style={{
                                                    textAlign: branding.footer.pageNumbers.position,
                                                    fontSize: '9pt',
                                                    color: '#666',
                                                    marginTop: '8px',
                                                }}
                                            >
                                                {branding.footer.pageNumbers.prefix || 'Page '}1 of
                                                ~X
                                                <span
                                                    style={{
                                                        fontSize: '9px',
                                                        color: '#9ca3af',
                                                        marginLeft: '8px',
                                                    }}
                                                >
                                                    (page numbers calculated in PDF)
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Box>

            <style>{`
        ${buildDocumentContentCss('.preview-content', branding)}
        .placeholder-value {
          background-color: #fef3c7;
          padding: 2px 4px;
          border-radius: 2px;
          font-weight: 500;
        }
        .page-break {
          display: block;
          height: 48px;
          margin: 4px 0;
          background-color: #e5e7eb;
          box-shadow: 9999px 0 0 #e5e7eb, -9999px 0 0 #e5e7eb;
          position: relative;
          overflow: hidden;
        }
        .page-break span { display: none; }
        .page-break::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: #c0c0c0;
        }
        .page-break::after {
          content: '— PAGE BREAK —';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: 10px;
          font-weight: 600;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          background: #e5e7eb;
          padding: 0 10px;
          white-space: nowrap;
        }
      `}</style>
        </Box>
    );
}
